import { PanelContent } from "../../lib/panel/panel-content";
import { useTranslation } from "react-i18next";
import { FormField } from "@babackapp/ui/components/form";
import { useWorkflowForm } from "../../context/workflows-editor-workflows-context";
import { useNodeFormPath } from "../../hooks/use-node-form-path";
import { AVAILABLE_LOCALES, TranslatableField } from "@babackapp/ui/components/translatable-field";
import { BlockEditorInput } from "@babackapp/blocks-editor/block-editor-input";
import { useVariablesDictionary } from "@babackapp/variables-registry";
import useCurrentShopSuspense from "../../hooks/use-current-shop-suspense";
import { useEffect, useMemo } from "react";
import { getDefaultWalletText, getDefaultWalletTitle } from "./default-translations";
import { Switch } from "@babackapp/ui/components/switch";
import { Label } from "@babackapp/ui/components/label";


export function PanelWalletNotification() {
  const { t } = useTranslation("trackoffice");
  const form = useWorkflowForm();
  const { data: shop } = useCurrentShopSuspense();
  const { variables } = useVariablesDictionary();

  const titleContentPath = useNodeFormPath("config.titleContent");
  const textContentPath = useNodeFormPath("config.textContent");
  const sendNotificationPath = useNodeFormPath("config.sendNotification");

  const availableLocales = useMemo(() => {
    const locales = shop.trackingPreferences?.availableLocales || ["en", "fr"];
    return locales.map((locale) => ({
      value: locale,
      label:
        AVAILABLE_LOCALES[locale as keyof typeof AVAILABLE_LOCALES]?.label ||
        locale,
      flag:
        AVAILABLE_LOCALES[locale as keyof typeof AVAILABLE_LOCALES]?.flag || "",
    }));
  }, [shop.trackingPreferences?.availableLocales]);

  const defaultLocale =
    shop.trackingPreferences?.notificationsDefaultLocale || "en";

  useEffect(() => {
    if (!titleContentPath || !textContentPath) return;

    const titleContent = form.getValues(titleContentPath);
    const textContent = form.getValues(textContentPath);

    const needsInitialization =
      !titleContent ||
      Object.keys(titleContent).length === 0 ||
      !textContent ||
      Object.keys(textContent).length === 0;

    if (needsInitialization) {
      const defaultTitleContent: Record<string, string> = {};
      const defaultTextContent: Record<string, string> = {};

      availableLocales.forEach((locale) => {
        defaultTitleContent[locale.value] = getDefaultWalletTitle(
          locale.value,
          shop?.trackingPreferences?.notificationsDefaultLocale || "en",
        );
        defaultTextContent[locale.value] = getDefaultWalletText(
          locale.value,
          shop?.trackingPreferences?.notificationsDefaultLocale || "en",
        );
      });

      if (!titleContent || Object.keys(titleContent).length === 0) {
        form.setValue(titleContentPath, defaultTitleContent, {
          shouldDirty: false,
        });
      }
      if (!textContent || Object.keys(textContent).length === 0) {
        form.setValue(textContentPath, defaultTextContent, {
          shouldDirty: false,
        });
      }
    }
  }, [titleContentPath, textContentPath, availableLocales, form]);

  if (!titleContentPath || !textContentPath) {
    return null;
  }

  return (
    <PanelContent title="Wallet Notification">
      <div className="space-y-4">
        <p className="text-xs text-muted-foreground">
          {t(
            "workflowsEditor.walletNotification.description",
            "Customize the title and text content of the wallet pass notification.",
          )}
        </p>

        {sendNotificationPath && (
          <FormField
            control={form.control}
            name={sendNotificationPath}
            render={({ field }) => (
              <div className="flex items-center justify-between space-x-2 rounded-lg border p-3">
                <div className="space-y-0.5">
                  <Label htmlFor="send-notification" className="text-sm font-medium">
                    {t(
                      "workflowsEditor.walletNotification.sendNotification",
                      "Send Push Notification",
                    )}
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {t(
                      "workflowsEditor.walletNotification.sendNotificationDescription",
                      "Enable to send push notification to wallet. If disabled, only the pass will be updated silently.",
                    )}
                  </p>
                </div>
                <Switch
                  id="send-notification"
                  checked={Boolean(field.value ?? true)}
                  onCheckedChange={field.onChange}
                />
              </div>
            )}
          />
        )}

        <FormField
          control={form.control}
          name={titleContentPath}
          render={({ field }) => (
            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <span className="font-medium inline-flex gap-1 items-center text-muted-foreground text-xs">
                  {t(
                    "workflowsEditor.walletNotification.titleContent",
                    "Title Content",
                  )}
                </span>
                <span className="text-[10px] text-muted-foreground">
                  {t(
                    "workflowsEditor.walletNotification.titleMaxChars",
                    "Max 60 chars",
                  )}
                </span>
              </div>
              <TranslatableField
                value={field.value as Record<string, string>}
                onChange={field.onChange}
                locales={availableLocales.map((l) => l.value)}
                defaultLocale={defaultLocale}
                placeholder={t(
                  "workflowsEditor.walletNotification.titlePlaceholder",
                  "Enter wallet pass title...",
                )}
                className="text-sm"
                renderInput={({ value, onChange, placeholder }) => {
                  const charCount = value?.length || 0;
                  const isNearLimit = charCount > 50;
                  const isAtLimit = charCount >= 60;

                  return (
                    <div className="space-y-1">
                      <BlockEditorInput
                        variables={variables}
                        value={value}
                        onChange={(newValue) => {
                          // Limit to 60 characters
                          if (newValue.length <= 60) {
                            onChange(newValue);
                          }
                        }}
                        placeholder={placeholder}
                        outputFormat="text"
                        className="pr-2"
                      />
                      <div className="flex justify-end">
                        <span
                          className={`text-[10px] ${
                            isAtLimit
                              ? "text-red-500 font-semibold"
                              : isNearLimit
                                ? "text-orange-500"
                                : "text-muted-foreground"
                          }`}
                        >
                          {charCount}/60
                        </span>
                      </div>
                    </div>
                  );
                }}
              />
            </div>
          )}
        />

        <FormField
          control={form.control}
          name={textContentPath}
          render={({ field }) => (
            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <span className="font-medium inline-flex gap-1 items-center text-muted-foreground text-xs">
                  {t(
                    "workflowsEditor.walletNotification.textContent",
                    "Text Content",
                  )}
                </span>
                <span className="text-[10px] text-muted-foreground">
                  {t(
                    "workflowsEditor.walletNotification.textMaxChars",
                    "Max 230 chars",
                  )}
                </span>
              </div>
              <TranslatableField
                value={field.value as Record<string, string>}
                onChange={field.onChange}
                locales={availableLocales.map((l) => l.value)}
                defaultLocale={defaultLocale}
                placeholder={t(
                  "workflowsEditor.walletNotification.textPlaceholder",
                  "Enter wallet pass text...",
                )}
                className="text-sm"
                renderInput={({ value, onChange, placeholder }) => {
                  const charCount = value?.length || 0;
                  const isNearLimit = charCount > 200;
                  const isAtLimit = charCount >= 230;

                  return (
                    <div className="space-y-1">
                      <BlockEditorInput
                        variables={variables}
                        value={value}
                        onChange={(newValue) => {
                          // Limit to 230 characters
                          if (newValue.length <= 230) {
                            onChange(newValue);
                          }
                        }}
                        placeholder={placeholder}
                        outputFormat="text"
                        className="pr-2"
                      />
                      <div className="flex justify-end">
                        <span
                          className={`text-[10px] ${
                            isAtLimit
                              ? "text-red-500 font-semibold"
                              : isNearLimit
                                ? "text-orange-500"
                                : "text-muted-foreground"
                          }`}
                        >
                          {charCount}/230
                        </span>
                      </div>
                    </div>
                  );
                }}
              />
            </div>
          )}
        />
      </div>
    </PanelContent>
  );
}
